import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    getApprovalWorkflow,
    getApprovalWorkflowStages,
    createApprovalWorkflow,
    editApprovalWorkflow,
    saveApprovalWorkflowStages,
    getWorkflowStageTypes,
    clearApprovalWorkflow
} from "../../../actions/approvalWorkflow";
import { getCompany } from "../../../actions/company";
import Modal from "../../common/Modal/Modal";
import { useSubmitGuard } from "../../common/Helpers";

export default function ApprovalWorkflowEditModal({ uuid, onClose, onSaved }) {
    const isNew = uuid === "new";
    const dispatch = useDispatch();

    const workflow = useSelector(state => state.approvalWorkflow.workflow);
    const loadedStages = useSelector(state => state.approvalWorkflow.stages);
    const stageTypes = useSelector(state => state.approvalWorkflow.stageTypes);
    //the acting company's plan-driven cap on stage count -- not a fixed number, see Company.max_approval_stages
    const company = useSelector(state => state.company.company);
    const maxApprovalStages = company.max_approval_stages || 4;

    const [name, setName] = useState("");
    const [stages, setStages] = useState([]);
    const [selectedStageTypeUuid, setSelectedStageTypeUuid] = useState("");
    const { isSubmitting, guardedSubmit } = useSubmitGuard();

    useEffect(() => {
        dispatch(getCompany());
        dispatch(getWorkflowStageTypes());
        if (!isNew) {
            dispatch(getApprovalWorkflow(uuid));
            dispatch(getApprovalWorkflowStages(uuid));
        }
        return () => dispatch(clearApprovalWorkflow());
    }, [dispatch, uuid, isNew]);

    useEffect(() => {
        if (!isNew && workflow.uuid === uuid) {
            setName(workflow.name || "");
        }
    }, [workflow, uuid, isNew]);

    useEffect(() => {
        if (!isNew) {
            setStages(loadedStages.map(s => ({ name: s.name, order: s.order, is_required: s.is_required })));
        }
    }, [loadedStages, isNew]);

    const renumber = (list) => list.map((s, index) => ({ ...s, order: index + 1 }));

    const handleAddStage = () => {
        const stageType = stageTypes.find(t => t.uuid === selectedStageTypeUuid);
        if (!stageType || stages.length >= maxApprovalStages) return;

        setStages(renumber([...stages, { name: stageType.name, order: stages.length + 1, is_required: true }]));
        setSelectedStageTypeUuid("");
    };

    const handleRemoveStage = (index) => {
        setStages(renumber(stages.filter((_, i) => i !== index)));
    };

    const handleMoveStage = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= stages.length) return;
        const reordered = [...stages];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setStages(renumber(reordered));
    };

    const handleToggleRequired = (index) => {
        setStages(stages.map((s, i) => i === index ? { ...s, is_required: !s.is_required } : s));
    };

    const handleSave = guardedSubmit((e) => {
        e.preventDefault();
        if (!name.trim() || stages.length < 1) return undefined;

        const saveStagesAndFinish = (templateUuid) => {
            return dispatch(saveApprovalWorkflowStages(templateUuid, stages)).then((saved) => {
                if (saved) onSaved();
            });
        };

        if (isNew) {
            return dispatch(createApprovalWorkflow({ name })).then((created) => {
                if (created) return saveStagesAndFinish(created.uuid);
            });
        } else {
            return dispatch(editApprovalWorkflow(uuid, { name })).then((edited) => {
                if (edited) return saveStagesAndFinish(uuid);
            });
        }
    });

    return (
        <Modal blurBack={true} hasCloseButton={true} hideLogo className="wide-form" handleClose={onClose} >
            <div className="flex-column w100" >
                <form onSubmit={handleSave} className="form" >
                    <h3 className="title2 gap-below-m heavy t-center">{isNew ? "New Approval Workflow" : "Edit Approval Workflow"}</h3>

                    <div className="inputs-wrapper">
                        <div className="input-basic">
                            <input id="workflow-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
                            <label htmlFor="workflow-name">Name<span> *</span></label>
                        </div>
                    </div>

                    <h3 className="content2 heavy-grey gap-below-xxs gap-above-m">Stages</h3>
                    {stages.map((stage, index) => (
                        <div key={index} className="flex-row space-btwn gap-below-xxs" style={{ flexWrap: "nowrap" }}>
                            <h4
                                className="content1 multiline light-black"
                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}
                            >{stage.name}</h4>
                            <div className="flex-row" style={{ flexWrap: "nowrap", flexShrink: 0 }}>
                                <button type="button" className="btn ter mini iblock gap-right-xs" onClick={() => handleMoveStage(index, -1)} disabled={index === 0}>Up</button>
                                <button type="button" className="btn ter mini iblock gap-right-xs" onClick={() => handleMoveStage(index, 1)} disabled={index === stages.length - 1}>Down</button>
                                <div className="checkbox-wrapper iblock gap-right-xs">
                                    <input
                                        className="typical-checkbox"
                                        type="checkbox"
                                        id={"required-" + index}
                                        checked={stage.is_required}
                                        onChange={() => handleToggleRequired(index)}
                                    />
                                    <label htmlFor={"required-" + index} className="content1 light-black">Required</label>
                                </div>
                                <button type="button" className="btn ter mini iblock" onClick={() => handleRemoveStage(index)}>Remove</button>
                            </div>
                        </div>
                    ))}

                    <div className="inputs-wrapper gap-above-s">
                        <div className="input-basic">
                            <select
                                id="add-stage-select"
                                style={selectedStageTypeUuid ? { color: "#3C3B41" } : { color: "#919197" }}
                                value={selectedStageTypeUuid}
                                onChange={(e) => setSelectedStageTypeUuid(e.target.value)}
                                disabled={stages.length >= maxApprovalStages}
                            >
                                <option value="">Select a stage</option>
                                {stageTypes.map(t => <option key={t.uuid} value={t.uuid}>{t.name}</option>)}
                            </select>
                            <label htmlFor="add-stage-select">Add a stage</label>
                        </div>
                    </div>
                    <button type="button" className="btn sec mini" onClick={handleAddStage} disabled={!selectedStageTypeUuid || stages.length >= maxApprovalStages}>Add Stage</button>
                    {stages.length >= maxApprovalStages &&
                        <h4 className="content1 multiline light gap-above-xxs t-center">Your plan allows a maximum of {maxApprovalStages} stages.</h4>
                    }

                    <div className="flex-row gap-above-m">
                        <button type="submit" className="btn pri expand gap-right-xs" disabled={isSubmitting}>{isSubmitting ? "Please wait..." : (isNew ? "Create Approval Workflow" : "Save changes")}</button>
                        <button type="button" className="btn sec expand" disabled={isSubmitting} onClick={onClose}>Cancel</button>
                    </div>
                </form>
            </div>
        </Modal>
    );
}
